import { parseCertificate, type ParsedCert } from '../x509/parse';
import { isIssuerOf } from './aia';
import type { DiscoveredChain } from './types';

/**
 * Completes a chain with the root it hangs from, taken from the operating system's trust store
 * (the desktop app hands the store over). Matches by issuer name and, when both sides carry key
 * identifiers, by AKI = SKI. Returns undefined when the chain already ends at a self-signed
 * certificate or no trusted root issued its last certificate.
 */
export function completeWithRoots(chain: Uint8Array[], roots: Uint8Array[]): DiscoveredChain | undefined {
  if (chain.length === 0) return undefined;
  const parsed = chain.map((d) => parseCertificate(d));
  const last = parsed[parsed.length - 1]!;
  if (last.subject === last.issuer) return undefined;
  for (const der of roots) {
    let root: ParsedCert;
    try {
      root = parseCertificate(der);
    } catch {
      continue;
    }
    if (root.subject !== last.issuer || !isIssuerOf(root, last)) continue;
    return {
      chain: [...chain, der],
      subjects: [...parsed.map((p) => p.subject), root.subject],
      endsAtRoot: root.subject === root.issuer,
      source: 'system-roots',
      detail: `root "${root.commonName}" from the operating system's trust store`,
    };
  }
  return undefined;
}
